import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

interface VendorProtectedRouteProps {
  children: React.ReactNode;
}

const SESSION_TIMEOUT_MS = 24 * 60 * 60 * 1000; // 24 hours

export default function VendorProtectedRoute({ children }: VendorProtectedRouteProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);

  useEffect(() => {
    const checkVendorAuth = () => {
      try {
        const userStr = localStorage.getItem('user');
        const userType = localStorage.getItem('userType');
        const sessionToken = localStorage.getItem('sessionToken');
        const loginTime = localStorage.getItem('vendorLoginTime');

        // No session at all
        if (!userStr || !sessionToken) {
          navigate('/vendor-login', { replace: true });
          setIsChecking(false);
          return;
        }

        // Check session expiry
        if (loginTime) {
          const elapsed = Date.now() - parseInt(loginTime, 10);
          if (elapsed > SESSION_TIMEOUT_MS) {
            // Session expired, clear everything
            localStorage.removeItem('user');
            localStorage.removeItem('sessionToken');
            localStorage.removeItem('userType');
            localStorage.removeItem('vendorLoginTime');
            navigate('/vendor-login', { replace: true });
            setIsChecking(false);
            return;
          }
        }

        const user = JSON.parse(userStr);
        
        // Verify user is a vendor
        if (userType !== 'vendor' && user.userType !== 'vendor') {
          localStorage.removeItem('user');
          localStorage.removeItem('sessionToken');
          localStorage.removeItem('userType');
          localStorage.removeItem('vendorLoginTime');
          navigate('/vendor-login', { replace: true });
          setIsChecking(false);
          return;
        }

        setIsAuthorized(true);
        setIsChecking(false);
      } catch (error) {
        console.error('Error parsing vendor session:', error);
        localStorage.removeItem('user');
        localStorage.removeItem('sessionToken');
        localStorage.removeItem('userType');
        localStorage.removeItem('vendorLoginTime');
        navigate('/vendor-login', { replace: true });
        setIsChecking(false);
      }
    };

    checkVendorAuth();
  }, [navigate, location.pathname]);

  // Show loading while checking auth
  if (isChecking) {
    return (
      <div className="min-h-screen bg-[#f5f5f7] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-red-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 text-sm font-medium">Verifying vendor access...</p>
        </div>
      </div>
    );
  }

  // If not authorized, don't render children (will redirect)
  if (!isAuthorized) {
    return null;
  }

  return <>{children}</>;
}